import { useCallback, useEffect, useMemo, useRef } from "react";
import camelCase from "lodash-es/camelCase";
import cx from "classnames";
import {
  Modal as AriaModal,
  Dialog as AriaDialog,
  ModalOverlay as AriaModalOverlay,
  ModalContext,
} from "react-aria-components";

import { ignoreOutsideClick } from "hooks/useOutsideClick";
import useAriaModalAttribute from "hooks/useAriaModalAttribute";

import styles from "./styles.module.css";
import { ModalInnerProps, ModalProps } from "./types";

const ModalInner = ({ children, role, ariaLabel, ariaDescribedBy, isExiting, onExit }: ModalInnerProps) => {
  const onExitRef = useRef(onExit);

  useEffect(() => {
    onExitRef.current = onExit;
  }, [onExit]);

  useEffect(() => {
    return () => {
      if (isExiting) {
        onExitRef.current?.();
      }
    };
  }, [isExiting]);

  const dialogRef = useAriaModalAttribute();

  return (
    <AriaDialog
      ref={dialogRef}
      role={role}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      className={styles.dialog}
    >
      {children}
    </AriaDialog>
  );
};

ModalInner.displayName = "DS.Modal.Inner";

const Modal = ({
  children,
  className,
  size = "default",
  withAnimation = true,
  role,
  ariaLabel,
  ariaDescribedBy,
  isOpen,
  onOpen,
  onClose,
  onExit,
  shouldIgnoreOutsideClick,
  isDismissable = true,
}: ModalProps) => {
  const handleOnOpenChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        onOpen?.();
      } else {
        onClose?.();
      }
    },
    [onOpen, onClose]
  );

  const modalContext = useMemo(() => ({ isDismissable }), [isDismissable]);

  return (
    <ModalContext.Provider value={modalContext}>
      <AriaModalOverlay
        isKeyboardDismissDisabled={!isDismissable}
        isOpen={isOpen}
        onOpenChange={handleOnOpenChange}
        className={({ isEntering, isExiting }) =>
          cx({
            [styles.enterAnimation]: isEntering,
            [styles.exitAnimation]: isExiting,
            [styles.overlay]: withAnimation,
          })
        }
        {...(shouldIgnoreOutsideClick ? ignoreOutsideClick : null)}
      >
        <AriaModal
          data-modal
          className={({ isEntering, isExiting }) =>
            cx(
              styles.modal,
              size && styles[camelCase(size)],
              withAnimation && {
                [styles.enterAnimation]: isEntering,
                [styles.exitAnimation]: isExiting,
              },
              className
            )
          }
        >
          {({ isExiting }) => (
            <ModalInner
              role={role}
              ariaLabel={ariaLabel}
              ariaDescribedBy={ariaDescribedBy}
              isExiting={isExiting}
              onExit={onExit}
            >
              {children}
            </ModalInner>
          )}
        </AriaModal>
      </AriaModalOverlay>
    </ModalContext.Provider>
  );
};

Modal.displayName = "DS.Modal";

export default Modal;
